import store from '@/store/store';
import User from '@/../bower_components/teknisavuecomponents/assets/interfaces/User';
import HttpRequest from '@/../bower_components/teknisavuecomponents/assets/utils/HttpRequest';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import router from '@/Router';

enum ModuloPermissao {
  MODULO_DASHBOARD = 'MODULO_DASHBOARD',
  MODULO_DEMONSTRATIVO = 'MODULO_DEMONSTRATIVO',
  MODULO_REGISTRO_PONTO_UNIDADE = 'MODULO_REGISTRO_PONTO_UNIDADE',
  MODULO_LANCAMENTO_DE_FOLHA = 'MODULO_LANCAMENTO_DE_FOLHA',
  MODULO_OCORRENCIAS = 'MODULO_OCORRENCIAS',
  MODULO_PROGRAMACAO_DE_FERIAS = 'MODULO_PROGRAMACAO_DE_FERIAS',
  MODULO_SOLICITACAO = 'MODULO_SOLICITACAO',
  MODULO_DOCUMENTOS = 'MODULO_DOCUMENTOS',
  MODULO_ADMISSAO = 'MODULO_ADMISSAO',
  MODULO_RELATORIO = 'MODULO_RELATORIO',
  MODULO_QUADRO_GERAL_DE_VINCULOS = 'MODULO_QUADRO_GERAL_DE_VINCULOS',
  MODULO_CAT_COMUNICADO_ACIDENTE_TRABALHO = 'MODULO_CAT_COMUNICADO_ACIDENTE_TRABALHO',
  MODULO_CONTROLE_DE_ENTREGA_DE_EPI = 'MODULO_CONTROLE_DE_ENTREGA_DE_EPI',
  MODULO_ORGANOGRAMA_DA_ORGANIZACAO = 'MODULO_ORGANOGRAMA_DA_ORGANIZACAO',
  MODULO_RECRUTAMENTO_E_SELECAO = 'MODULO_RECRUTAMENTO_E_SELECAO',
  MODULO_BIOMETRIA_FACIAL = 'MODULO_BIOMETRIA_FACIAL'
}

enum ActionPermissao {
  IDVISUALIZAR = 'IDVISUALIZAR',
  IDCRIAR = 'IDCRIAR',
  IDATUALIZAR = 'IDATUALIZAR',
  IDDELETAR = 'IDDELETAR'
}

interface ControlePermissao {
  module: string,
  action: string
}

// #region [ CONTROLE_WRAPPER ]

/**
 * Classe para controle de acesso, validando permissões antes de executar ações.
 * Cada ação (visualizar, criar, atualizar, deletar) é envolvida com a validação de permissão
 * antes de permitir que a função seja executada. Se o módulo não for informado, o módulo default 
 * será extraído da rota atual (meta do routes).
 */
class ControleAcessoWrapper {
  /**
   * Obtém o módulo default da rota atual.
   * @returns {string|null} O módulo extraído da rota ou null se não encontrado.
   */
  static getModuloDefault() {
    let modulo = null;
    try {
      const route = router && router.app && router.app.$route;
      if (route && route.meta && route.meta.permission) {
        modulo = route.meta.permission.module;
      }
      return modulo;
    } catch (error) {
      return modulo;
    }
  }

  static visualizar(func: Function, module = null) {
    const modulo = module || ControleAcessoWrapper.getModuloDefault();
    const action = ActionPermissao.IDVISUALIZAR;
    return ControleAcessoWrapper.validarPermissao(action, modulo, func);
  }

  static criar(func: Function, module = null) {
    const modulo = module || ControleAcessoWrapper.getModuloDefault();
    const action = ActionPermissao.IDCRIAR;
    return ControleAcessoWrapper.validarPermissao(action, modulo, func);
  }

  static atualizar(func: Function, module = null) {
    const modulo = module || ControleAcessoWrapper.getModuloDefault();
    const action = ActionPermissao.IDATUALIZAR;
    return ControleAcessoWrapper.validarPermissao(action, modulo, func);
  }

  static deletar(func: Function, module = null) {
    const modulo = module || ControleAcessoWrapper.getModuloDefault();
    const action = ActionPermissao.IDDELETAR;
    return ControleAcessoWrapper.validarPermissao(action, modulo, func);
  }

  static validarPermissao(action: ActionPermissao, modulo: ModuloPermissao, func: Function) {
    return function(...args) {
      try {
        // Chama a função de validação de permissão do ControleAcessoController
        ControleAcessoController.validarPermissao(action, modulo);
        return func(...args); // Executa a função passada com os argumentos
      } catch (error) {

      }
    };
  }
}





// #region [ CONTROLE_ACESSO ]

class ControleAcessoController {

/**
 * Verifica se o usuário tem permissão para executar uma ação em um módulo específico.
 * Retorna `true` nos seguintes casos:
 *   - O usuário é um operador DP (ADM).
 *   - O controle de acesso está desativado.
 *   - O perfil de acesso do usuário possui a permissão necessária para a ação no módulo.
 * Caso contrário, retorna `false`.
 * 
 * @param {string} module - O ID do módulo (Ex: "MODULO_DASHBOARD").
 * @param {string} action - A ação a ser verificada (Ex: "IDVISUALIZAR", "IDCRIAR", etc.).
 * @returns {boolean} Retorna `true` se o usuário tiver permissão, `false` caso contrário.
 */
  public static temPermissao(module: string, action: string) {
    const permissoes = store.getters.permissoes;
    const isDp = store.getters.user.dp;
    const controleAcessoAtivado = store.getters.user.idativacontroleacesso;

    // retorna true caso seja ADM ou o controle de acesso esteja desativado
    if(isDp || !controleAcessoAtivado) return true;

    if (!permissoes || permissoes.length === 0) {
      return false;
    }

    // Verifica se o usuário tem permissão para a ação no módulo especificado
    return permissoes.some(
      (permissao) =>
        permissao.IDCONTROLEMODULO === module && permissao[action] === true
    );
  }

  /**
   * Valida se o usuário tem permissão para realizar uma determinada ação em um módulo.
   * Se não tiver permissão, exibe uma mensagem de erro e interrompe a execução lançando uma exceção.
   * 
   * @param {string} module - O ID do módulo (Ex: "MODULO_DASHBOARD").
   * @param {string} action - A ação a ser verificada (Ex: "IDVISUALIZAR", "IDCRIAR", etc.).
   * @throws {Error} Lança um erro se o usuário não tiver permissão para a ação especificada.
   */
  public static validarPermissao(action: string, module = null) {
    const modulo = module || this.getModuloDefault();

    const temPermissao = this.temPermissao(modulo, action);

    if (!temPermissao) {
      const msg = 'Permissão necessária: O usuário não possui permissão para realizar essa ação.';
      SnackbarUtils.showMessage(msg, SnackbarUtils.TYPE_INFO);
      console.warn(`Ação bloqueada pelo controle de acesso!: Módulo: ${modulo} - Ação: ${action}`);
      throw new Error(msg);
    }
  }

  public static verificarControlePermissaoRota(controlePermissao: ControlePermissao, next: Function) {
    if (controlePermissao) {
      const { module, action } = controlePermissao;
  
      if (!ControleAcessoController.temPermissao(module, action)) {

        try {
          SnackbarUtils.showMessage('Permissão necessária: O usuário não tem permissão para acessar esta tela.', SnackbarUtils.TYPE_INFO);
        } catch (error) {}

        return next("/");
      }
    }
  }

  /**
 * Obtém o módulo default da rota atual.
 * @returns {string|null} O módulo extraído da rota ou null se não encontrado.
 */
  static getModuloDefault() {
    let modulo = null;
    try {
      const route = router && router.app && router.app.$route;
      if (route && route.meta && route.meta.permission) {
        modulo = route.meta.permission.module;
      }
      return modulo;
    } catch (error) {
      return modulo;
    }
  }
  

  // #region [ REQUESTS ]

  public static async getPerfisAcessoControle(
    showLoading = true,
  ): Promise<any> {
    const route = 'getPerfisAcessoControle';

    const user: User = store.getters.user;
    const nrOrg = user.nrorg;

    const params = {
      requestType: 'FilterData',
      NRORG: nrOrg,
    };

    const response = await HttpRequest.get(route, params, showLoading);
    const perfisAcesso: any = response.data.dataset.data;

    return perfisAcesso;
  };

  public static async getPermissoesAcessoPerfil(
    nrPerfilAcesso: number,
    projeto: string,
    showLoading = true,
  ): Promise<any> {
    const route = 'getPermissoesAcessoPerfil';

    const user: User = store.getters.user;
    const nrOrg = user.nrorg;
    const cdOperador = user.cdoperador;


    const params = {
      requestType: 'FilterData',
      NRORG: nrOrg,
      CDOPERADOR: cdOperador,
      NRPERFILACESSO: nrPerfilAcesso,
      PROJETO: projeto
    };

    try {

      const response = await HttpRequest.get(route, params, showLoading);
      const perfisAcesso: any = response.data.dataset.data;
  
      return perfisAcesso;
  
    } catch (err) {
      const defaultError = 'Não foi possível obter as permissões de acesso.';
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  };

  public static async atualizarPermissoesAcesso(
    nrPerfilAcesso: number,
    permissoes: any,
    showLoading = true,
  ) {
    const route = 'atualizarPermissoesAcesso';

    const user: User = store.getters.user;
    const nrOrg = user.nrorg;
    const cdOperador = user.cdoperador;

    const params = {
      requestType: 'Row',
      NRORG: nrOrg,
      row: {
        CDOPERADOR: cdOperador,
        NRPERFILACESSO: nrPerfilAcesso,
        PERMISSOES: permissoes
      },
    };

    try {

      await HttpRequest.post(route, params, showLoading);
  
    } catch (err) {
      const defaultError = 'Não foi possível atualizar as permissões de acesso.';
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  }

  public static async atualizarStatusControleAcesso(
    status: "S" | "N",
    showLoading = true,
  ) {
    const route = 'atualizarStatusControleAcesso';

    const user: User = store.getters.user;
    const nrOrg = user.nrorg;
    const cdOperador = user.cdoperador;

    const params = {
      requestType: 'Row',
      NRORG: nrOrg,
      row: {
        CDOPERADOR: cdOperador,
        STATUS: status
      },
    };

    try {

      await HttpRequest.post(route, params, showLoading);
  
    } catch (err) {
      const defaultError = 'Não foi possível atualizar o status do controle de acesso.';
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  }

  public static async getStatusControleAcesso(
    showLoading = true,
  ): Promise<any> {
    const route = 'getStatusControleAcesso';

    const user: User = store.getters.user;
    const nrOrg = user.nrorg;
    const cdOperador = user.cdoperador;

    const params = {
      requestType: 'FilterData',
      NRORG: nrOrg,
      CDOPERADOR: cdOperador
    };

    try {

      const response = await HttpRequest.get(route, params, showLoading);
      const data: any = response.data.dataset.data;
  
      return data && data.status;
  
    } catch (err) {
      const defaultError = 'Não foi possível obter o status do controle de acesso.';
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  };

}

export { ControleAcessoController, ControleAcessoWrapper, ModuloPermissao, ActionPermissao };
